import { useSeoMeta } from '@unhead/react';
import { Bitcoin, MapPin, Users, Calendar, Zap, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { MeetupCard } from '@/components/MeetupCard';
import { useCalendarEvents } from '@/hooks/useCalendarEvents';
import { LoginArea } from '@/components/auth/LoginArea';

const LOGO_URL = 'https://blossom.ditto.pub/05f4879585280485207cab71b229bf01abfaaed3e78462bf4d1b67802f01d90d.png';
const BANNER_URL = 'https://image.nostr.build/64916511aab4c93a7058d647c956aba42a83c9073c1ccc68ac5135c3e60759da.jpg';

function MeetupSkeleton() {
  return (
    <Card className="border-white/10 bg-[#0a0a0a] overflow-hidden">
      <Skeleton className="h-44 w-full rounded-none bg-white/5" />
      <div className="p-5 space-y-3">
        <Skeleton className="h-5 w-3/4 bg-white/5" />
        <Skeleton className="h-4 w-1/2 bg-white/5" />
        <Skeleton className="h-4 w-2/3 bg-white/5" />
        <Skeleton className="h-4 w-full bg-white/5" />
        <Skeleton className="h-4 w-4/5 bg-white/5" />
        <Skeleton className="h-9 w-full mt-2 bg-white/5" />
      </div>
    </Card>
  );
}

const Index = () => {
  useSeoMeta({
    title: 'PerrisPlebs — Bitcoin-Only Meetup in Perris, California',
    description: 'Join PerrisPlebs, a bi-monthly Bitcoin-only meetup in Perris, California. We promote Bitcoin as an electronic cash system and meet only at venues that accept Bitcoin payments.',
  });

  const { data, isLoading, isError } = useCalendarEvents();

  const upcomingEvents = data?.upcoming ?? [];
  const pastEvents = data?.past ?? [];
  const totalEvents = (data?.all ?? []).length;

  return (
    <div className="min-h-screen bg-[#080808] text-white">

      {/* ── NAV ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="PerrisPlebs Logo"
              className="h-8 w-8 rounded-md object-cover"
            />
            <span className="font-bold text-white text-lg tracking-tight">PerrisPlebs</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="#meetups" className="text-sm text-white/60 hover:text-white transition-colors hidden sm:block">
              Meetups
            </a>
            <a href="#about" className="text-sm text-white/60 hover:text-white transition-colors hidden sm:block">
              About
            </a>
            <LoginArea className="max-w-48" />
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-14">
        {/* Banner background with parallax-style overlay */}
        <div className="absolute inset-0 isolate">
          <img
            src={BANNER_URL}
            alt="PerrisPlebs Banner"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-[#080808]" />
          {/* Blue glow orbs */}
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-[#2596be]/10 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-[#2596be]/8 rounded-full blur-3xl -z-10" />
        </div>

        <div className="relative z-10 flex flex-col items-center text-center px-4 max-w-4xl mx-auto space-y-8">
          {/* Logo */}
          <div className="animate-fade-in-up">
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-[#2596be]/20 rounded-2xl blur-xl" />
              <img
                src={LOGO_URL}
                alt="PerrisPlebs"
                className="relative w-40 h-40 rounded-2xl object-cover shadow-2xl border border-[#2596be]/30"
              />
            </div>
          </div>

          {/* Title */}
          <div className="animate-fade-in-up-delay-1 space-y-2">
            <h1 className="text-5xl sm:text-7xl font-black tracking-tight text-white">
              Perris<span className="text-[#2596be] glow-blue-text">Plebs</span>
            </h1>
            <div className="flex items-center justify-center gap-2 text-white/50 text-sm font-medium tracking-widest uppercase">
              <MapPin className="h-3.5 w-3.5 text-[#2596be]" />
              <span>Perris, California</span>
            </div>
          </div>

          {/* Welcome paragraph */}
          <div className="animate-fade-in-up-delay-2 max-w-2xl">
            <p className="text-lg sm:text-xl text-white/75 leading-relaxed">
              Welcome to <strong className="text-white">Perris Plebs</strong>,{' '}
              <strong className="text-[#2596be]">a Bitcoin-Only Meetup in Perris, California</strong>.
              Join us as we promote the use of Bitcoin as an electronic cash system.{' '}
              <strong className="text-white">Meetings will only be held at places that accept Bitcoin for payments</strong>.
            </p>
          </div>

          {/* Stats row */}
          <div className="animate-fade-in-up-delay-3 flex items-center gap-6 sm:gap-10">
            <div className="flex flex-col items-center gap-1">
              <span className="text-3xl font-black text-[#2596be]">
                {isLoading ? '—' : totalEvents}
              </span>
              <span className="text-xs text-white/40 uppercase tracking-widest">Total Meetups</span>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div className="flex flex-col items-center gap-1">
              <span className="text-3xl font-black text-[#2596be]">
                {isLoading ? '—' : upcomingEvents.length}
              </span>
              <span className="text-xs text-white/40 uppercase tracking-widest">Upcoming</span>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div className="flex flex-col items-center gap-1">
              <Bitcoin className="h-8 w-8 text-[#2596be]" />
              <span className="text-xs text-white/40 uppercase tracking-widest">Only</span>
            </div>
          </div>

          {/* Scroll hint */}
          <a
            href="#meetups"
            className="animate-fade-in-up-delay-3 flex flex-col items-center gap-2 text-white/30 hover:text-[#2596be] transition-colors mt-4"
          >
            <span className="text-xs uppercase tracking-widest">See Meetups</span>
            <ChevronDown className="h-5 w-5 animate-bounce" />
          </a>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="py-20 px-4 bg-[#080808]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: <Bitcoin className="h-7 w-7 text-[#2596be]" />,
                title: 'Bitcoin-Only',
                desc: 'We are focused exclusively on Bitcoin as a peer-to-peer electronic cash system, just as Satoshi intended.',
              },
              {
                icon: <MapPin className="h-7 w-7 text-[#2596be]" />,
                title: 'Bitcoin-Accepting Venues',
                desc: 'Every meetup is held at a local business in Perris, CA that accepts Bitcoin as payment — putting BTC to work.',
              },
              {
                icon: <Users className="h-7 w-7 text-[#2596be]" />,
                title: 'Community First',
                desc: 'A welcoming bi-monthly gathering for bitcoiners of all experience levels in the Inland Empire.',
              },
            ].map((item) => (
              <Card
                key={item.title}
                className="bg-[#0d0d0d] border-white/8 hover:border-[#2596be]/30 transition-all duration-300 group"
              >
                <CardContent className="pt-6 pb-6 space-y-3">
                  <div className="h-12 w-12 rounded-xl bg-[#2596be]/10 flex items-center justify-center group-hover:bg-[#2596be]/20 transition-colors duration-300">
                    {item.icon}
                  </div>
                  <h3 className="font-bold text-white text-lg">{item.title}</h3>
                  <p className="text-white/55 text-sm leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── MEETUPS FEED ── */}
      <section id="meetups" className="py-20 px-4 bg-[#050505]">
        <div className="max-w-6xl mx-auto">

          {/* Section header */}
          <div className="mb-12 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-px w-8 bg-[#2596be]" />
                <span className="text-[#2596be] text-xs font-bold uppercase tracking-widest">NIP-52 Calendar Events</span>
              </div>
              <h2 className="text-4xl font-black text-white">Meetups</h2>
            </div>
            <div className="flex items-center gap-2 text-white/40 text-sm">
              <Zap className="h-4 w-4 text-[#2596be]" />
              <span>Powered by Nostr</span>
            </div>
          </div>

          {/* Upcoming */}
          <div className="mb-14">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
              <Calendar className="h-5 w-5 text-[#2596be]" />
              Upcoming Events
              {!isLoading && upcomingEvents.length > 0 && (
                <span className="text-sm font-normal text-white/40">({upcomingEvents.length})</span>
              )}
            </h3>

            {isLoading && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => <MeetupSkeleton key={i} />)}
              </div>
            )}

            {!isLoading && isError && (
              <Card className="border-dashed border-white/10 bg-transparent">
                <CardContent className="py-12 text-center">
                  <p className="text-white/40 text-sm">
                    Could not load meetups. Check your relay connections and try again.
                  </p>
                </CardContent>
              </Card>
            )}

            {!isLoading && !isError && upcomingEvents.length === 0 && (
              <Card className="border-dashed border-[#2596be]/20 bg-[#2596be]/5">
                <CardContent className="py-12 text-center space-y-2">
                  <Calendar className="h-10 w-10 text-[#2596be]/40 mx-auto" />
                  <p className="text-white/50 text-sm">No upcoming meetups yet. Check back soon!</p>
                  <p className="text-white/30 text-xs">
                    Events are published on Nostr via NIP-52 (kinds 31922 &amp; 31923)
                  </p>
                </CardContent>
              </Card>
            )}

            {!isLoading && !isError && upcomingEvents.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {upcomingEvents.map((event, i) => (
                  <MeetupCard key={event.id} event={event} featured={i === 0} />
                ))}
              </div>
            )}
          </div>

          {/* Past events */}
          {!isLoading && pastEvents.length > 0 && (
            <div>
              <h3 className="text-xl font-bold text-white/60 mb-6 flex items-center gap-3">
                <Calendar className="h-5 w-5 text-white/30" />
                Past Events
                <span className="text-sm font-normal text-white/30">({pastEvents.length})</span>
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {pastEvents.map((event) => (
                  <MeetupCard key={event.id} event={event} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-white/5 py-10 px-4 bg-black">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/30">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="PerrisPlebs" className="h-6 w-6 rounded-md object-cover" />
            <span className="font-semibold text-white/50">PerrisPlebs</span>
            <span>· Perris, California</span>
          </div>
          <div className="flex items-center gap-1">
            <Bitcoin className="h-3.5 w-3.5 text-[#2596be]" />
            <span>Bitcoin-Only Meetup · Built on Nostr ·{' '}</span>
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2596be]/60 hover:text-[#2596be] transition-colors"
            >
              Vibed with Shakespeare
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
